import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link,
  ButtonBase
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import ArrowBackIosNewRounded from "@mui/icons-material/ArrowBackIosNewRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import UploadFileRounded from "@mui/icons-material/UploadFileRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";

import {
  parseSymphony,
  exportSymphony,
  resolvePath,
  childrenOf,
  isDrillable,
  nodeTitle,
  nodeSubtitle,
  stepKind,
  weightLabel,
  countDescendants,
  countAssets
} from "@/lib/composer-symphony";
import type { SymphonyNode, SymphonyStep } from "@/lib/composer-symphony";

// ---- Per-kind tile color --------------------------------------------------

function kindColor(step: SymphonyStep): string {
  switch (step) {
    case "root":
      return "primary.main";
    case "wt-cash-equal":
    case "wt-cash-specified":
    case "wt-inverse-vol":
      return "info.main";
    case "group":
      return "secondary.main";
    case "if":
    case "if-child":
      return "warning.main";
    case "asset":
      return "success.main";
  }
}

/** Short breadcrumb label — keeps crumbs compact so the row never scrolls. */
function crumbLabel(n: SymphonyNode): string {
  switch (n.step) {
    case "root":
      return "Symphony";
    case "group":
      return n.name || "Group";
    case "asset":
      return n.ticker || "Asset";
    case "if":
      return "IF / ELSE";
    case "if-child":
      return n["is-else-condition?"] ? "ELSE" : "Branch";
    default:
      return "Weight";
  }
}

// ---- Page -----------------------------------------------------------------

/**
 * Algo Builder (Phase 5.1, Sergey 2026-05-21) — imports a Composer-style
 * symphony JSON and presents it as a collapsible, paginated tree:
 * every page shows one node's direct children; drilling into a branch
 * opens that node as a new page with breadcrumb navigation, so the tree
 * is browsable on a phone with no horizontal scroll.
 *
 * This phase is import + view + export. IF/ELSE editing and the Wizard
 * builder come next.
 */
export function AlgoBuilderPage() {
  const navigate = useNavigate();
  const [root, setRoot] = useState<SymphonyNode | null>(null);
  const [pathIds, setPathIds] = useState<string[]>([]);
  const [rawText, setRawText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function loadFromText(text: string) {
    const result = parseSymphony(text);
    if (!result.ok || !result.root) {
      setError(result.error ?? "Couldn't read that symphony.");
      return;
    }
    setRoot(result.root);
    setPathIds([]);
    setError(null);
    setRawText("");
  }

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => loadFromText(String(reader.result ?? ""));
    reader.onerror = () => setError("Couldn't read that file.");
    reader.readAsText(file);
  }

  // ---- Import view ----
  if (!root) {
    return (
      <Box sx={{ maxWidth: 680, mx: "auto", px: 2, py: 3 }}>
        <Button
          startIcon={<ArrowBackIosNewRounded sx={{ fontSize: 14 }} />}
          onClick={() => navigate(-1)}
          size="small"
          sx={{ mb: 2, color: "text.secondary" }}
        >
          Back
        </Button>

        <Typography variant="h2" sx={{ mb: 0.5 }}>
          Algo Builder
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Import a symphony JSON to view its IF/ELSE rebalancing logic as a
          browsable tree. Editing and the wizard builder are on the way.
        </Typography>

        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={<UploadFileRounded />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ fontWeight: 700, borderRadius: 2, py: 1.25 }}
        >
          Import symphony JSON file
        </Button>
        <Box
          component="input"
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = "";
          }}
          sx={{ display: "none" }}
        />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", my: 2 }}
        >
          — or paste the JSON —
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={6}
          maxRows={14}
          placeholder='{ "step": "root", "name": "...", "children": [ ... ] }'
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12 } }}
        />
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          disabled={!rawText.trim()}
          onClick={() => loadFromText(rawText)}
          sx={{ mt: 1.5, fontWeight: 700, borderRadius: 2 }}
        >
          Load pasted JSON
        </Button>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }

  // ---- Tree view ----
  const chain = resolvePath(root, pathIds);
  const current = chain[chain.length - 1] ?? root;
  const kids = childrenOf(current);

  return (
    <Box sx={{ maxWidth: 760, mx: "auto", px: 2, py: 2 }}>
      {/* Symphony header */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
            {root.name || "Symphony"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {countAssets(root)} assets · {countDescendants(root)} nodes
            {root.rebalance ? ` · rebalances ${root.rebalance}` : ""}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <IconButton
            aria-label="Copy JSON"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(exportSymphony(root));
                setToast("Symphony JSON copied to clipboard.");
              } catch {
                setToast("Couldn't access the clipboard.");
              }
            }}
            sx={(t) => ({
              border: `1px solid ${t.palette.divider}`,
              borderRadius: 1.5
            })}
          >
            <ContentCopyRounded sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            aria-label="Download JSON"
            onClick={() => {
              const blob = new Blob([exportSymphony(root)], {
                type: "application/json"
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${(root.name || "symphony").replace(
                /[^a-z0-9]+/gi,
                "-"
              )}.json`;
              a.click();
              URL.revokeObjectURL(url);
              setToast("Symphony JSON downloaded.");
            }}
            sx={(t) => ({
              border: `1px solid ${t.palette.divider}`,
              borderRadius: 1.5
            })}
          >
            <DownloadRounded sx={{ fontSize: 18 }} />
          </IconButton>
          <Button
            size="small"
            onClick={() => {
              setRoot(null);
              setPathIds([]);
            }}
            sx={{ fontWeight: 700 }}
          >
            New
          </Button>
        </Stack>
      </Stack>

      {/* Breadcrumb — collapses in the middle so it never scrolls */}
      <Breadcrumbs
        maxItems={3}
        separator={<ChevronRightRounded sx={{ fontSize: 16 }} />}
        sx={{ mb: 1.5, "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" } }}
      >
        {chain.map((n, i) =>
          i === chain.length - 1 ? (
            <Typography
              key={n.id}
              variant="body2"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {crumbLabel(n)}
            </Typography>
          ) : (
            <Link
              key={n.id}
              component="button"
              type="button"
              variant="body2"
              onClick={() => setPathIds(pathIds.slice(0, i))}
              sx={{ fontWeight: 600 }}
            >
              {crumbLabel(n)}
            </Link>
          )
        )}
      </Breadcrumbs>

      {/* Current-node header card */}
      <Box
        sx={(t) => ({
          p: 1.5,
          mb: 1.5,
          borderRadius: 2,
          bgcolor: alpha(t.palette.text.primary, 0.04),
          borderLeft: `3px solid ${
            kindColorHex(current.step, t) ?? t.palette.primary.main
          }`
        })}
      >
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.4 }}
        >
          {stepKind(current.step)}
        </Typography>
        <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {nodeTitle(current)}
        </Typography>
        {nodeSubtitle(current) && (
          <Typography variant="body2" color="text.secondary">
            {nodeSubtitle(current)}
          </Typography>
        )}
        {pathIds.length > 0 && (
          <Button
            size="small"
            startIcon={<ArrowBackIosNewRounded sx={{ fontSize: 12 }} />}
            onClick={() => setPathIds(pathIds.slice(0, -1))}
            sx={{ mt: 0.5, ml: -0.5, fontWeight: 700 }}
          >
            Up one level
          </Button>
        )}
      </Box>

      {/* Children of the current node */}
      {kids.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          <AccountTreeOutlined sx={{ fontSize: 36, opacity: 0.5 }} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            This node has no children — it's a leaf.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {kids.map((child) => (
            <NodeRow
              key={child.id}
              node={child}
              onOpen={
                isDrillable(child)
                  ? () => setPathIds([...pathIds, child.id])
                  : undefined
              }
            />
          ))}
        </Stack>
      )}

      <Snackbar
        open={toast !== null}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ width: "100%" }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ---- Node row -------------------------------------------------------------

/** Resolve a kind color to a concrete hex off the theme. */
function kindColorHex(step: SymphonyStep, t: Theme): string {
  const path = kindColor(step);
  const [group, shade] = path.split(".") as [keyof Theme["palette"], string];
  const p = t.palette[group] as unknown as Record<string, string>;
  return p?.[shade] ?? t.palette.primary.main;
}

function NodeRow({
  node,
  onOpen
}: {
  node: SymphonyNode;
  onOpen?: () => void;
}) {
  const kids = childrenOf(node);
  const wt = weightLabel(node.weight);
  const inner = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{ width: "100%", textAlign: "left" }}
    >
      {/* kind tile */}
      <Box
        sx={(t) => ({
          width: 8,
          alignSelf: "stretch",
          minHeight: 40,
          borderRadius: 1,
          bgcolor: kindColorHex(node.step, t),
          flexShrink: 0
        })}
      />
      <Box sx={{ flex: 1, minWidth: 0, py: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: "text.secondary" }}
          >
            {stepKind(node.step)}
          </Typography>
          {wt && (
            <Box
              sx={(t) => ({
                px: 0.6,
                borderRadius: 0.75,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: alpha(t.palette.primary.main, 0.16),
                color: "primary.main"
              })}
            >
              {wt}
            </Box>
          )}
        </Stack>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, lineHeight: 1.3, wordBreak: "break-word" }}
        >
          {nodeTitle(node)}
        </Typography>
        {nodeSubtitle(node) && (
          <Typography variant="caption" color="text.secondary">
            {nodeSubtitle(node)}
          </Typography>
        )}
      </Box>
      {kids.length > 0 && (
        <Stack alignItems="center" sx={{ flexShrink: 0, pr: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: "text.secondary" }}
          >
            {kids.length}
          </Typography>
          {onOpen && (
            <ChevronRightRounded
              sx={{ fontSize: 20, color: "text.secondary" }}
            />
          )}
        </Stack>
      )}
    </Stack>
  );

  const sx = (t: Theme) => ({
    display: "block",
    width: "100%",
    borderRadius: 2,
    border: `1px solid ${t.palette.divider}`,
    px: 1,
    ...(onOpen ? { "&:hover": { bgcolor: "action.hover" } } : {})
  });

  return onOpen ? (
    <ButtonBase onClick={onOpen} sx={sx}>
      {inner}
    </ButtonBase>
  ) : (
    <Box sx={sx}>{inner}</Box>
  );
}
